import PrizeBoxDTO from "@common/dtos/boxes/PrizeBox";
import OpMachineDTO from "@common/dtos/OpMachineDTO";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardHeader, MDBIcon, MDBListGroup, MDBListGroupItem } from "mdb-react-ui-kit";
import { act, useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { StatusIcon } from "../../../../../../../components/StatusIcon";
import { PrizeBoxIndividualDoorItem, PrizeBoxIndividualDoorItemProps } from "./PrizeBoxIndividualDoorItem";
import API from "../../../../../../../service/api";
import { MachineAction } from "../../../../../../../components/editable/MachineAction";
import MachineOrBoxActionDTO from "@common/dtos/MachineOrBoxActionDTO";
import { ConfirmAction } from "../../../../../../../components/editable/ConfirmAction";

export interface PrizeBoxViewerProps {
    machine: OpMachineDTO;
    box: PrizeBoxDTO;
    apiURI: string;
    onClose: () => void;
}

export const PrizeBoxViewer = ({
    machine,
    box,
    apiURI,
    onClose
}: PrizeBoxViewerProps)=>{
    const { t } = useTranslation();

    const Header = useMemo(() => (
        <div className="d-flex flex-column w-100">
            <span className="d-flex w-100 justify-content-center align-items-center text-center" style={{fontWeight: 'bold'}}>
                <span><MDBIcon icon="gift" color="primary" className="me-1"/>{t('prize_box')}</span>
            </span>
            {box.status.length > 0 &&
                <span className="d-flex w-100 justify-content-center align-items-center" style={{flexWrap: "wrap"}}>
                    {box.status.map(s =>
                        <div key={s.id} style={{display: 'inline'}}>
                            <ConfirmAction
                                title={t('clear_status')}
                                confirmText={t('clear')}
                                prompt={<span><Trans i18nKey={'clear_status_prompt'} values={{statusTitle: t(`status:${s.id}`)}}/></span>}
                                action={async () => await API.POST(`${apiURI}/prize/${box.id}/statusclear`, { status: s.id })}
                            >
                                { open => 
                                    <StatusIcon badge name={s.id} ts={s.ts} onClick={open}/>
                                }
                            </ConfirmAction>

                        </div>
                    )}
                </span>
            }
        </div>
    ), [box, box.status]);

    return (
        <div className="d-flex flex-column px-3 pt-3 flex-fill position-relative w-100">
            <MDBCard className={`w-100`} style={{}}>
                <div
                    className="position-absolute d-flex justify-content-center w-100"
                    style={{ top: 0, marginTop: -10 }}
                >
                    <span className="badge rounded-pill badge-primary">
                        {box.id+1}
                    </span>
                </div>

                <MDBCardHeader className="w-100">
                    <span className="position-absolute" style={{fontWeight: 'bold'}}>
                        <MDBBtn tag="a" color="none" className="px-2 py-1" size="lg" onClick={onClose}><MDBIcon icon="chevron-left"/></MDBBtn>
                    </span>
                    <div className="d-flex flex-row justify-content-center align-items-center w-100">
                        { Header }
                    </div>
                    
                </MDBCardHeader>
                <MDBCardBody className="p-2">
                    <PrizeBoxViewerContent machine={machine} box={box} apiURI={apiURI}/>
                </MDBCardBody>
            </MDBCard>
        </div>
    )
}

type PrizeBoxDoorProps = PrizeBoxIndividualDoorItemProps & {id: number};

const PrizeBoxViewerContent = ({
    machine,
    box,
    apiURI
}:{
    machine: OpMachineDTO,
    box: PrizeBoxDTO,
    apiURI: string
}) => {

    const [selectedDoor, setSelectedDoor] = useState<PrizeBoxDoorProps|null>(null);

    const onDoorClick = useCallback((door: PrizeBoxDoorProps) => setSelectedDoor(door.id == selectedDoor?.id ? null : door), []);

    useEffect(() => {
        if (selectedDoor) setSelectedDoor(box.doors.find(d => d.id == selectedDoor.id) ?? null);
    }, [box.doors]);

    return (
        <div className="d-flex flex-row">
            <div className="d-flex flex-row pe-2" style={{borderRight: '1px solid rgba(0,0,0,0.25)'}}>
                <div className="d-flex flex-column">
                    {box.doors.slice(0, Math.floor(box.doors.length/2)).map(d =>
                        <PrizeBoxIndividualDoorItem key={d.id} {...d} className="card-hover2" onClick={() => onDoorClick(d)} selected={d.id == selectedDoor?.id}/>
                    )}
                </div>
                <div className="d-flex flex-column">
                    {box.doors.slice(Math.floor(box.doors.length/2)).map(d =>
                        <PrizeBoxIndividualDoorItem key={d.id} {...d} className="card-hover2" onClick={() => onDoorClick(d)} selected={d.id == selectedDoor?.id}/>
                    )}
                </div>
            </div>
            {selectedDoor && <PrizeBoxDoorViewer
                key={selectedDoor.id}
                box={box}
                door={selectedDoor}
                apiURI={apiURI}
                machine={machine}
            />}
        </div>
    )
}

const PrizeBoxDoorViewer = ({
    machine,
    box,
    door,
    apiURI
}: {
    machine: OpMachineDTO,
    box: PrizeBoxDTO,
    door: PrizeBoxDoorProps,
    apiURI: string
}) => {
    const { t } = useTranslation();

    return (
        <div className="d-flex flex-column w-100 p-2">

            <MDBListGroup light className="p-0 w-100">
                <MDBListGroupItem className="p-1 d-flex flex-row align-items-center">
                    <span className="fw-bold me-1">{t('status_title')}:</span>
                    { door.status.map(s => (
                        <div key={s.id} style={{display: 'inline'}}>
                            <ConfirmAction
                                title={t('clear_status')}
                                confirmText={t('clear')}
                                prompt={<span><Trans i18nKey={'clear_status_prompt'} values={{statusTitle: t(`status:${s.id}`)}}/></span>}
                                action={async () => await API.POST(`${apiURI}/prize/${box.id}/${door.id}/statusclear`, { status: s.id })}
                            >
                                { open => 
                                    <StatusIcon badge name={s.id} ts={s.ts} onClick={open}/>
                                }
                            </ConfirmAction>

                        </div>
                    )) }
                    {  door.status.length == 0 && <span className="badge badge-primary ms-1">{t('ok')}</span> }
                </MDBListGroupItem>
                <MDBListGroupItem className="p-1 d-flex flex-row align-items-center">
                    <span className="fw-bold me-1">{t('filled')}:</span>
                    <span className={`badge badge-${door.filled?'success':'danger'}`}>{t(door.filled ? 'yes' : 'no')}</span>
                </MDBListGroupItem>
            </MDBListGroup>

            {/* Actiuni */}
            {machine.pactions.length > 0 && <div className="d-flex flex-column">
                <span className="text-muted">{t('actions')}</span>
                <MDBListGroup light style={{maxHeight: 200, overflow: 'auto'}}>
                    {machine.pactions.map(action => (
                        <MDBListGroupItem key={action.id} className="py-1 ps-2">
                            <MachineAction
                                action={action}
                                onDone={async () => await API.POST(`${apiURI}/prize/${box.id}/${door.id}/action`, { id: action.id })}
                            >
                                {open => (
                                    <MDBBtn color="link" className="p-1" onClick={open}>
                                        <MDBIcon icon={action.icon || "wrench"} className="me-1"/>
                                        <span>{t(action.title)}</span>
                                    </MDBBtn>    
                                )}
                            </MachineAction>
                            
                        </MDBListGroupItem>
                    ))}
                </MDBListGroup>
            </div>}
        </div>
    )
}